// =============================================================================
// API клиент. Все authenticated запросы добавляют X-Telegram-InitData header.
// initData достаём из window.Telegram.WebApp.initData (или из ?initData=
// в URL для dev-режима).
// =============================================================================

const API_BASE =
  process.env.NEXT_PUBLIC_API_URL || 'https://api.prognoz.app';

const FP_STORAGE_KEY = 'prognoz_fp_v1';

/**
 * Sync чтение fingerprint из localStorage. Используется внутри `fetchJson`
 * (которое не может быть async на момент построения headers без рефакторинга).
 * Async расчёт происходит в `lib/fingerprint.ts` и складывается в этот же
 * ключ — этот хелпер просто читает кэш.
 */
function readCachedFingerprint(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = localStorage.getItem(FP_STORAGE_KEY);
    return v && /^[A-Za-z0-9]{16,64}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

export interface MarketListItem {
  id: string;
  category: string;
  country_code: string | null;
  question_ru: string;
  hero_image_r2_key: string | null;
  open_at: number;
  close_at: number;
  state: string;
  pool_yes_micro: number;
  pool_no_micro: number;
  unique_betters_yes: number;
  unique_betters_no: number;
  bets_count: number;
  fee_bps: number;
  is_breaking: number;
  is_featured: number;
  resolved_at?: number | null;
}

export interface MarketDetail {
  id: string;
  category: string;
  country_code: string | null;
  question_ru: string;
  question_en: string | null;
  description_md: string | null;
  rules_md: string;
  source: { url: string; tier: number; name: string };
  fallback_rule: string | null;
  media: {
    hero_image: string | null;
    hero_video: string | null;
    youtube_url: string | null;
    gallery: string[];
  };
  timeline: {
    open_at: number;
    close_at: number;
    resolve_deadline: number;
    resolved_at: number | null;
  };
  state: string;
  pools: {
    yes_micro: number;
    no_micro: number;
    unique_betters_yes: number;
    unique_betters_no: number;
    bets_count: number;
  };
  config: {
    fee_bps: number;
    min_bet_micro: number;
    max_bet_micro: number;
    max_bet_pct_of_side_bps: number;
  };
  flags: { is_breaking: boolean; is_featured: boolean };
  resolution: {
    id: string;
    outcome: 'yes' | 'no' | 'refund';
    evidence_url: string;
    evidence_quote: string | null;
    evidence_screenshot_r2_key: string | null;
    resolved_by: number;
    created_at: number;
    dispute_window_until: number;
    finalized_at: number | null;
  } | null;
  recent_bets: Array<{
    id: string;
    side: number;
    amount_micro: number;
    created_at: number;
    first_name: string;
    username: string | null;
  }>;
}

export interface MeResponse {
  user: {
    telegram_id: number;
    username: string | null;
    first_name: string;
    language_code: string;
    ton_address: string | null;
    balances: { usdt_micro: number };
    stats: {
      bets_count: number;
      wins_count: number;
      total_volume_usd_cents: number;
      total_profit_usd_cents: number;
    };
    referral: { code: string | null; earnings_usd_cents: number };
    is_flagged: boolean;
    created_at: number;
  };
  agreement: {
    accepted: boolean;
    current_terms_version: string;
    current_privacy_version: string;
    accepted_at: number | null;
  };
}

export interface AgreementStatusResponse {
  accepted: boolean;
  current_terms_version: string;
  current_privacy_version: string;
  agreement: {
    terms_version: string;
    privacy_version: string;
    accepted_at: number;
  } | null;
}

export interface AcceptAgreementInput {
  confirmed_age_18: true;
  confirmed_jurisdiction: true;
  confirmed_terms_accepted: true;
}

export interface BetResponse {
  prediction_id: string;
  new_pools: { yes_micro: number; no_micro: number };
  payout_estimate: {
    net_winnings_micro: number;
    total_payout_micro: number;
    roi_bps: number;
  };
}

export interface UserBet {
  prediction_id: string;
  market_id: string;
  market_question: string;
  market_category: string;
  market_state: string;
  market_fee_bps: number;
  market_close_at: number;
  market_resolved_at: number | null;
  side: number;
  amount_micro: number;
  asset: string;
  status: 'active' | 'won' | 'lost' | 'refunded';
  payout_micro: number | null;
  fee_micro: number | null;
  created_at: number;
  paid_at: number | null;
  pool_yes_at_bet_micro: number;
  pool_no_at_bet_micro: number;
  current_pool_yes_micro: number;
  current_pool_no_micro: number;
}

export interface UserBetsResponse {
  bets: UserBet[];
  aggregate: {
    bets_count: number;
    active_count: number;
    won_count: number;
    lost_count: number;
    refunded_count: number;
    total_at_stake_micro: number;
    total_volume_micro: number;
    total_payouts_micro: number;
  };
}

export interface UserTransaction {
  id: string;
  user_id: number;
  type: string;
  amount_micro: number;
  asset: string;
  external_tx_hash: string | null;
  external_address: string | null;
  external_provider: string | null;
  related_market_id: string | null;
  related_prediction_id: string | null;
  status: string;
  notes: string | null;
  created_at: number;
  confirmed_at: number | null;
}

export interface DepositIntent {
  id: string;
  user_id: number;
  asset: 'TON' | 'USDT_TRC20' | 'USDT_TON';
  amount_native: number;
  amount_usd_micro: number;
  fx_rate: number;
  deposit_address: string;
  memo: string;
  status: 'pending' | 'confirmed' | 'expired' | 'cancelled';
  expires_at: number;
  created_at: number;
}

export interface AdminMarketInput {
  category: string;
  country_code: string | null;
  question_ru: string;
  question_en?: string | null;
  description_md?: string | null;
  rules_md: string;
  source_url: string;
  source_tier: 1 | 2 | 3;
  source_name: string;
  fallback_rule?: string | null;
  open_at: number;
  close_at: number;
  resolve_deadline: number;
  pre_seed_yes_micro?: number;
  pre_seed_no_micro?: number;
  fee_bps?: number;
  is_breaking?: boolean;
  is_featured?: boolean;
  youtube_url?: string | null;
}

export interface ApiError {
  error: { code: string; reason?: string; message?: string };
}

class ApiClient {
  constructor(private getInitData: () => string | null) {}

  private async fetchJson<T>(
    path: string,
    options: RequestInit = {},
    auth = false,
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (auth) {
      const initData = this.getInitData();
      if (!initData) {
        throw new ApiClientError('AUTH_NOT_READY', 'initData not available yet');
      }
      headers['X-Telegram-InitData'] = initData;
      // Anti-multiaccount fingerprint. Best-effort: если кеш уже прогрет —
      // header ставится синхронно, иначе пропускаем (server gracefully
      // обработает первый раз без fingerprint, второй раз уже будет).
      const fp = readCachedFingerprint();
      if (fp) headers['X-Device-Fingerprint'] = fp;
    }

    const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        // ignore — пусть код ниже отрепортит как HTTP error
      }
    }

    if (!res.ok) {
      const errBody = body as ApiError | null;
      throw new ApiClientError(
        errBody?.error?.code ?? `HTTP_${res.status}`,
        errBody?.error?.message ?? `Request failed with ${res.status}`,
        body,
      );
    }
    return body as T;
  }

  // --- Public ---
  // Public — без initData
  fxRate() {
    return this.fetchJson<{ ton_usd: number; updated_at: number | null; is_fallback: boolean }>(
      '/api/public/fx-rate',
      {},
      false,
    );
  }

  listMarkets(params: { limit?: number; cursor?: string; category?: string } = {}) {
    const q = new URLSearchParams();
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.category) q.set('category', params.category);
    return this.fetchJson<{ items: MarketListItem[]; next_cursor: string | null }>(
      `/api/public/markets${q.toString() ? `?${q}` : ''}`,
    );
  }

  getMarket(id: string) {
    return this.fetchJson<{ market: MarketDetail }>(
      `/api/public/markets/${encodeURIComponent(id)}`,
    );
  }

  // Global recent-bets feed (для LiveTicker'а на /markets).
  recentBets(limit = 20) {
    return this.fetchJson<{
      bets: Array<{
        id: string;
        side: 0 | 1;
        amount_micro: number;
        created_at: number;
        first_name: string;
        username: string | null;
        category: string;
      }>;
    }>(`/api/public/markets/recent-bets?limit=${limit}`, {}, false);
  }

  // --- Authenticated ---
  me() {
    return this.fetchJson<MeResponse>('/api/me', {}, true);
  }

  agreementStatus() {
    return this.fetchJson<AgreementStatusResponse>('/api/agreement', {}, true);
  }

  acceptAgreement(input: AcceptAgreementInput) {
    return this.fetchJson<{
      accepted: true;
      agreement: {
        terms_version: string;
        privacy_version: string;
        accepted_at: number;
      };
    }>(
      '/api/agreement/accept',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  }

  myBets(limit = 50) {
    return this.fetchJson<UserBetsResponse>(`/api/me/bets?limit=${limit}`, {}, true);
  }

  // Сохранить TON-адрес после connect через TON Connect.
  saveTonAddress(address: string) {
    return this.fetchJson<{ ok: true; address: string }>(
      '/api/me/ton-address',
      { method: 'POST', body: JSON.stringify({ address }) },
      true,
    );
  }

  myTransactions(limit = 50) {
    return this.fetchJson<{ transactions: UserTransaction[] }>(
      `/api/me/transactions?limit=${limit}`,
      {},
      true,
    );
  }

  myDisputes() {
    return this.fetchJson<{
      disputes: Array<{
        id: string;
        market_id: string;
        resolution_id: string;
        reason: string;
        evidence_url: string | null;
        status: 'open' | 'accepted' | 'rejected';
        review_notes: string | null;
        created_at: number;
        reviewed_at: number | null;
        question_ru: string;
        market_state: string;
        outcome: string;
      }>;
    }>('/api/me/disputes', {}, true);
  }

  myCommunityStats() {
    return this.fetchJson<{
      total_active_users: number;
      me: {
        bets_count: number;
        volume_usd_cents: number;
        profit_usd_cents: number;
        win_rate: number;
      };
      percentiles: {
        bets: number | null;
        volume: number | null;
        profit: number | null;
        win_rate: number | null;
      };
      averages: {
        bets: number | null;
        volume_cents: number | null;
        win_rate: number | null;
      };
    }>('/api/me/community-stats', {}, true);
  }

  myPending() {
    return this.fetchJson<{
      withdrawals: Array<{
        id: string;
        amount_micro: number;
        asset: string;
        external_address: string;
        status: string;
        created_at: number;
      }>;
      deposits: Array<{
        id: string;
        asset: string;
        amount_native: number;
        amount_usd_micro: number;
        deposit_address: string;
        memo: string;
        expires_at: number;
        created_at: number;
      }>;
      summary: {
        withdrawals_count: number;
        withdrawals_total_micro: number;
        deposits_count: number;
      };
    }>('/api/me/pending', {}, true);
  }

  leaderboard(period: 'weekly' | 'monthly' | 'all_time' = 'all_time', limit = 20) {
    return this.fetchJson<{
      period: string;
      top: Array<{
        rank: number;
        telegram_id: number;
        username: string | null;
        first_name: string;
        total_profit_usd_cents: number;
        total_volume_usd_cents: number;
        bets_count: number;
        wins_count: number;
      }>;
      me: {
        rank: number | null;
        total: number;
        telegram_id: number;
        first_name: string;
        username: string | null;
        total_profit_usd_cents: number;
        total_volume_usd_cents: number;
        bets_count: number;
        wins_count: number;
      } | null;
    }>(`/api/leaderboard?period=${period}&limit=${limit}`, {}, true);
  }

  placeBet(input: {
    market_id: string;
    side: 0 | 1;
    amount_micro: number;
    asset?: string;
    /** Optional UUID для дедупа double-clicks/network retries. */
    idempotency_key?: string;
  }) {
    return this.fetchJson<BetResponse>(
      '/api/bet',
      {
        method: 'POST',
        body: JSON.stringify({
          market_id: input.market_id,
          side: input.side,
          amount_micro: input.amount_micro,
          asset: input.asset ?? 'USDT_TRC20',
          ...(input.idempotency_key ? { idempotency_key: input.idempotency_key } : {}),
        }),
      },
      true,
    );
  }

  createDepositIntent(input: {
    asset: 'TON' | 'USDT_TRC20';
    amount_native: number;
    /** Tron-кошелёк отправителя для USDT crypto-bind. Optional. */
    expected_from_address?: string;
  }) {
    return this.fetchJson<{ intent: DepositIntent }>(
      '/api/deposit/intent',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  }

  listPendingIntents() {
    return this.fetchJson<{ intents: DepositIntent[] }>('/api/deposit/intents', {}, true);
  }

  confirmUsdtDeposit(intentId: string, txHash: string) {
    return this.fetchJson<{ ok: true; transaction_id: string; credited_micro: number }>(
      '/api/deposit/usdt/confirm',
      {
        method: 'POST',
        body: JSON.stringify({ intent_id: intentId, tx_hash: txHash }),
      },
      true,
    );
  }

  withdraw(input: {
    amount_micro: number;
    asset: 'TON' | 'USDT_TRC20';
    address: string;
    /** Optional UUID для дедупа двойных submit'ов. */
    idempotency_key?: string;
  }) {
    return this.fetchJson<{
      transaction_id: string;
      status: string;
      review: string;
      estimated_time: string;
      idempotent_replay?: boolean;
    }>(
      '/api/withdraw',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  }

  // --- Admin ---
  adminMe() {
    return this.fetchJson<{ operator: { telegram_id: number; role: string; is_active: boolean } | null }>(
      '/api/admin/me',
      {},
      true,
    );
  }

  adminListMarkets(state?: string) {
    const q = state ? `?state=${encodeURIComponent(state)}` : '';
    return this.fetchJson<{ markets: Array<{
      id: string; category: string; question_ru: string; state: string;
      pool_yes_micro: number; pool_no_micro: number; bets_count: number;
      close_at: number; resolve_deadline: number;
    }> }>(`/api/admin/markets${q}`, {}, true);
  }

  adminGetMarket(id: string) {
    return this.fetchJson<{ market: Record<string, unknown>; predictions: Array<Record<string, unknown>> }>(
      `/api/admin/markets/${encodeURIComponent(id)}`,
      {},
      true,
    );
  }

  adminResolve(id: string, body: { outcome: 'yes' | 'no' | 'refund'; evidence_url: string; evidence_quote?: string; evidence_screenshot_r2_key?: string; notes?: string }) {
    return this.fetchJson<{ resolution: Record<string, unknown> }>(
      `/api/admin/markets/${encodeURIComponent(id)}/resolve`,
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  async adminUploadEvidence(file: File): Promise<{ key: string; url: string }> {
    const initData = this.getInitData();
    if (!initData) {
      throw new ApiClientError('AUTH_NOT_READY', 'initData not available yet');
    }
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/api/admin/upload/evidence`, {
      method: 'POST',
      headers: { 'X-Telegram-InitData': initData },
      body: fd,
    });
    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        /* ignore */
      }
    }
    if (!res.ok) {
      const err = body as ApiError | null;
      throw new ApiClientError(
        err?.error?.code ?? `HTTP_${res.status}`,
        err?.error?.message ?? `Upload failed: ${res.status}`,
        body,
      );
    }
    return body as { key: string; url: string };
  }

  async adminUploadHero(marketId: string, file: File): Promise<{ key: string; url: string }> {
    const initData = this.getInitData();
    if (!initData) {
      throw new ApiClientError('AUTH_NOT_READY', 'initData not available yet');
    }
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(
      `${API_BASE}/api/admin/markets/${encodeURIComponent(marketId)}/hero`,
      {
        method: 'POST',
        headers: { 'X-Telegram-InitData': initData },
        body: fd,
      },
    );
    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        /* ignore */
      }
    }
    if (!res.ok) {
      const err = body as ApiError | null;
      throw new ApiClientError(
        err?.error?.code ?? `HTTP_${res.status}`,
        err?.error?.message ?? `Upload failed: ${res.status}`,
        body,
      );
    }
    return body as { key: string; url: string };
  }

  async adminUploadVideo(marketId: string, file: File): Promise<{ key: string; url: string }> {
    const initData = this.getInitData();
    if (!initData) {
      throw new ApiClientError('AUTH_NOT_READY', 'initData not available yet');
    }
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(
      `${API_BASE}/api/admin/markets/${encodeURIComponent(marketId)}/video`,
      {
        method: 'POST',
        headers: { 'X-Telegram-InitData': initData },
        body: fd,
      },
    );
    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        /* ignore */
      }
    }
    if (!res.ok) {
      const err = body as ApiError | null;
      throw new ApiClientError(
        err?.error?.code ?? `HTTP_${res.status}`,
        err?.error?.message ?? `Upload failed: ${res.status}`,
        body,
      );
    }
    return body as { key: string; url: string };
  }

  /** Возвращает абсолютный URL для R2-объекта (для использования в <img src>). */
  mediaUrl(key: string, bucket: 'media' | 'evidence' = 'media'): string {
    return `${API_BASE}/media/${bucket}/${key}`;
  }

  adminCreateMarket(body: AdminMarketInput) {
    return this.fetchJson<{ market_id: string }>(
      '/api/admin/markets',
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  adminUpdateMarket(id: string, body: AdminMarketInput) {
    return this.fetchJson<{ ok: true }>(
      `/api/admin/markets/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify(body) },
      true,
    );
  }

  adminListDisputes() {
    return this.fetchJson<{
      disputes: Array<{
        id: string;
        resolution_id: string;
        market_id: string;
        user_id: number;
        reason: string;
        evidence_url: string | null;
        status: string;
        created_at: number;
        question_ru: string;
        market_state: string;
        outcome: string;
        username: string | null;
        first_name: string;
      }>;
    }>('/api/admin/disputes', {}, true);
  }

  adminAcceptDispute(id: string, notes?: string) {
    return this.fetchJson<{ ok: true }>(
      `/api/admin/disputes/${encodeURIComponent(id)}/accept`,
      { method: 'POST', body: JSON.stringify({ notes: notes ?? null }) },
      true,
    );
  }

  adminRejectDispute(id: string, notes?: string) {
    return this.fetchJson<{ ok: true }>(
      `/api/admin/disputes/${encodeURIComponent(id)}/reject`,
      { method: 'POST', body: JSON.stringify({ notes: notes ?? null }) },
      true,
    );
  }

  createDispute(body: { market_id: string; reason: string; evidence_url?: string }) {
    return this.fetchJson<{ dispute_id: string; status: string }>(
      '/api/disputes',
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  adminUserSearch(q: string) {
    return this.fetchJson<{
      users: Array<{
        telegram_id: number;
        username: string | null;
        first_name: string;
        balance_usdt_micro: number;
        balance_ton_nano: number;
        bets_count: number;
        wins_count: number;
        total_volume_usd_cents: number;
        total_profit_usd_cents: number;
        is_flagged: number;
        flag_reason: string | null;
        created_at: number;
        last_active_at: number;
      }>;
    }>(`/api/admin/users/search?q=${encodeURIComponent(q)}`, {}, true);
  }

  adminCreditUser(userId: number, body: { amount_micro: number; asset?: string; reason?: string }) {
    return this.fetchJson<{ ok: true; transaction_id: string }>(
      `/api/admin/users/${userId}/credit`,
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  adminFlagUser(userId: number, body: { is_flagged: boolean; flag_reason?: string }) {
    return this.fetchJson<{ ok: true }>(
      `/api/admin/users/${userId}/flag`,
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  adminListWithdrawals() {
    return this.fetchJson<{ withdrawals: Array<{
      id: string; user_id: number; amount_micro: number; asset: string;
      external_address: string; status: string; created_at: number;
      username: string | null; first_name: string;
    }> }>('/api/admin/withdrawals', {}, true);
  }

  adminApproveWithdrawal(id: string, body: { tx_hash?: string; notes?: string }) {
    return this.fetchJson<{ ok: true }>(
      `/api/admin/withdrawals/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  adminRejectWithdrawal(id: string, body: { reason?: string }) {
    return this.fetchJson<{ ok: true }>(
      `/api/admin/withdrawals/${encodeURIComponent(id)}/reject`,
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  // --- Treasury reconciliation ---
  adminTreasuryLatest() {
    return this.fetchJson<{
      snapshot: {
        snapshot_at: number;
        hot_ton_nano: number;
        hot_usdt_micro: number;
        ton_usd_rate: number;
        total_assets_usd_micro: number;
        user_balances_usd_micro: number;
        open_pools_usd_micro: number;
        pending_withdrawals_micro: number;
        total_liabilities_micro: number;
        surplus_micro: number;
        is_healthy: number;
        notes: string | null;
      } | null;
    }>('/api/admin/treasury/latest', {}, true);
  }

  adminTreasuryHistory(hours = 24) {
    return this.fetchJson<{
      hours: number;
      snapshots: Array<{
        snapshot_at: number;
        total_assets_usd_micro: number;
        total_liabilities_micro: number;
        surplus_micro: number;
        is_healthy: number;
        ton_usd_rate: number;
      }>;
    }>(`/api/admin/treasury/history?hours=${hours}`, {}, true);
  }

  // --- Orphan deposits (lost-and-found) ---
  adminListOrphanDeposits(status: 'pending' | 'credited' | 'ignored' = 'pending') {
    return this.fetchJson<{
      orphans: Array<{
        id: number;
        external_tx_hash: string;
        asset: 'TON' | 'USDT_TRC20';
        amount_raw: number;
        source_address: string | null;
        memo_raw: string | null;
        reason: string;
        status: string;
        detected_at: number;
        resolved_at: number | null;
        resolved_by: number | null;
        resolved_notes: string | null;
        matched_user_id: number | null;
        matched_tx_id: string | null;
      }>;
    }>(`/api/admin/orphan-deposits?status=${status}`, {}, true);
  }

  adminCreditOrphan(id: number, body: { user_id: number; notes?: string }) {
    return this.fetchJson<{ ok: true; transaction_id: string; credited_micro: number }>(
      `/api/admin/orphan-deposits/${id}/credit`,
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  adminIgnoreOrphan(id: number, body: { notes?: string } = {}) {
    return this.fetchJson<{ ok: true }>(
      `/api/admin/orphan-deposits/${id}/ignore`,
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }

  // --- Audit log ---
  adminAuditList(filters: {
    action?: string;
    target_type?: string;
    operator_id?: number;
    since?: number;
    limit?: number;
  } = {}) {
    const params = new URLSearchParams();
    if (filters.action) params.set('action', filters.action);
    if (filters.target_type) params.set('target_type', filters.target_type);
    if (filters.operator_id) params.set('operator_id', String(filters.operator_id));
    if (filters.since) params.set('since', String(filters.since));
    if (filters.limit) params.set('limit', String(filters.limit));
    const q = params.toString();
    return this.fetchJson<{
      limit: number;
      events: Array<{
        id: number;
        operator_id: number;
        action: string;
        target_type: string;
        target_id: string;
        before_json: string | null;
        after_json: string | null;
        created_at: number;
        operator_first_name: string | null;
        operator_username: string | null;
      }>;
    }>(`/api/admin/audit${q ? '?' + q : ''}`, {}, true);
  }

  adminAuditFacets() {
    return this.fetchJson<{
      actions: Array<{ action: string; count: number }>;
      target_types: Array<{ target_type: string; count: number }>;
    }>('/api/admin/audit/facets', {}, true);
  }

  // --- Status snapshot (comprehensive health для admin/status page) ---
  adminStatus() {
    return this.fetchJson<{
      ts: number;
      api: { ok: boolean; cron_schedule: string };
      db: {
        users_count: number;
        markets_open: number;
        markets_total: number;
        predictions_active: number;
        transactions_today: number;
        orphan_deposits_pending: number;
      };
      treasury: {
        ts: number;
        ton_usd_rate: number;
        hot: { total_usd_micro: number };
        warm: { total_usd_micro: number; configured: boolean };
        cold: { total_usd_micro: number; configured: boolean };
        operator: { total_usd_micro: number; configured: boolean };
        internal: {
          user_balances_micro: number;
          active_pool_micro: number;
          fees_total_micro: number;
          fees_unswept_micro: number;
          deposits_minus_withdrawals_micro: number;
        };
        hot_overflow_usd_micro: number;
        drift_usd_micro: number;
      };
      cron: {
        fx_rate_last_at: number | null;
        ton_watcher_last_lt: string | null;
        reconcile_last_at: number | null;
        reconcile_last_drift_micro: number | null;
        daily_metrics_last_at: number | null;
        backup_last_at: number | null;
        drain_check_last_at: number | null;
        mirror_betting_last_at: number | null;
        ip_correlation_last_at: number | null;
        velocity_baseline_last_at: number | null;
        sybil_detection_last_at: number | null;
      };
      anti_abuse: {
        open_total: number;
        open_critical: number;
        open_warn: number;
        open_info: number;
        by_type: Array<{ signal_type: string; count: number }>;
      };
      payouts: {
        kill_switch: 'on' | 'off';
        daily_total_micro: number;
        queue_auto_pending: number;
        queue_signing: number;
        queue_broadcasting: number;
        queue_manual_pending: number;
        queue_kill_switched: number;
        auto_failed_24h: number;
        confirmed_24h: number;
      };
      bindings: {
        ton_signer: { configured: boolean; reachable: boolean | null };
        tron_signer: { configured: boolean; reachable: boolean | null };
      };
      activity_24h: {
        deposits_count: number;
        deposits_total_usd_micro: number;
        withdrawals_count: number;
        withdrawals_total_usd_micro: number;
        bets_count: number;
        bets_volume_micro: number;
        new_users: number;
      };
    }>('/api/admin/status', {}, true);
  }

  // --- Global metrics dashboard ---
  adminMetrics() {
    return this.fetchJson<{
      users: { total: number; new_7d: number; dau: number; wau: number; mau: number };
      bets: {
        last_24h: { bets: number; volume: number };
        last_7d: { bets: number; volume: number };
        last_30d: { bets: number; volume: number };
      };
      fees: { total_collected_micro: number };
      markets: {
        by_state: Array<{ state: string; n: number }>;
        top_by_volume: Array<{
          id: string;
          question_ru: string;
          category: string;
          state: string;
          volume: number;
          bets_count: number;
        }>;
        top_by_bets: Array<{
          id: string;
          question_ru: string;
          category: string;
          state: string;
          volume: number;
          bets_count: number;
        }>;
      };
      growth_14d: Array<{ day_bucket: number; bets: number; volume: number }>;
      operator_activity: { actions_7d: number };
      generated_at: number;
    }>('/api/admin/metrics', {}, true);
  }

  // --- Treasury drains (hot ↔ cold) ---
  adminListDrains(limit = 50) {
    return this.fetchJson<{
      drains: Array<{
        id: number;
        operator_id: number;
        asset: 'TON' | 'USDT_TRC20';
        amount_raw: number;
        direction: 'hot_to_cold' | 'cold_to_hot';
        external_tx_hash: string | null;
        notes: string | null;
        created_at: number;
        operator_first_name: string | null;
        operator_username: string | null;
      }>;
    }>(`/api/admin/treasury/drains?limit=${limit}`, {}, true);
  }

  adminCreateDrain(body: {
    asset: 'TON' | 'USDT_TRC20';
    amount_raw: number;
    direction: 'hot_to_cold' | 'cold_to_hot';
    external_tx_hash?: string;
    notes?: string;
  }) {
    return this.fetchJson<{ ok: true; id: number }>(
      '/api/admin/treasury/drains',
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
  }
}

export class ApiClientError extends Error {
  constructor(
    public code: string,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export function createApiClient(getInitData: () => string | null): ApiClient {
  return new ApiClient(getInitData);
}

export type { ApiClient };
